import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  AppConfig,
  APP_CONFIG,
  QuickbooksAccount,
  QuickbooksVendor,
  HTTPTraceHeader,
  TraceService,
  SpanTypes,
} from '@sidkik/global';
import { Observable, catchError, tap, throwError } from 'rxjs';

export interface QuickbooksAPI {
  getVendors(trace?: HTTPTraceHeader): Observable<QuickbooksVendor[]>;
  getAccounts(trace?: HTTPTraceHeader): Observable<QuickbooksAccount[]>;
  getAuthURI(trace?: HTTPTraceHeader): Observable<string>;
}

@Injectable({
  providedIn: 'root',
})
export class QuickbooksService implements QuickbooksAPI {
  constructor(
    @Inject(APP_CONFIG) readonly tenantConfig: AppConfig,
    private readonly http: HttpClient,
    private readonly traceService: TraceService
  ) {}

  private processHeaders(trace?: HTTPTraceHeader): HttpHeaders {
    let headers = new HttpHeaders({
      'ngsw-bypass': 'bypass',
      'Cache-Control': 'no-cache',
    });

    if (trace) {
      headers = new HttpHeaders({
        Traceparent: trace?.traceparent ?? '',
        Tracestate: trace?.tracestate ?? '',
        'ngsw-bypass': 'bypass',
        'Cache-Control': 'no-cache',
        'Access-Control-Expose-Headers':
          'Traceparent,TraceState,Orig-Tracestate,Orig-Traceparent,X-Tp', // expose the trace headers in response
      });
    }
    return headers;
  }

  getVendors(trace?: HTTPTraceHeader): Observable<QuickbooksVendor[]> {
    let internalTrace = false;
    if (!trace) {
      this.traceService.startSpan(SpanTypes.adminQBGetVendors);
      trace = this.traceService.getHTTPHeaderPropagators(
        SpanTypes.adminQBGetVendors
      );
      internalTrace = true;
    }
    return this.http
      .get<QuickbooksVendor[]>(
        `${this.tenantConfig.api.endpoint}/admin/quickbooks/vendors`,
        {
          headers: this.processHeaders(trace),
        }
      )
      .pipe(
        catchError((err: any) => {
          internalTrace &&
            this.traceService.endSpan(SpanTypes.adminQBGetVendors, err);
          return throwError(() => err);
        }),
        tap(() => {
          internalTrace &&
            this.traceService.endSpan(SpanTypes.adminQBGetVendors);
        })
      );
  }
  getAccounts(trace?: HTTPTraceHeader): Observable<QuickbooksAccount[]> {
    let internalTrace = false;
    if (!trace) {
      this.traceService.startSpan(SpanTypes.adminQBGetAccounts);
      trace = this.traceService.getHTTPHeaderPropagators(
        SpanTypes.adminQBGetAccounts
      );
      internalTrace = true;
    }
    return this.http
      .get<QuickbooksAccount[]>(
        `${this.tenantConfig.api.endpoint}/admin/quickbooks/accounts`,
        {
          headers: this.processHeaders(trace),
        }
      )
      .pipe(
        catchError((err: any) => {
          internalTrace &&
            this.traceService.endSpan(SpanTypes.adminQBGetAccounts, err);
          return throwError(() => err);
        }),
        tap(() => {
          internalTrace &&
            this.traceService.endSpan(SpanTypes.adminQBGetAccounts);
        })
      );
  }
  createAccount(
    account: QuickbooksAccount,
    trace?: HTTPTraceHeader
  ): Observable<QuickbooksAccount> {
    let internalTrace = false;
    if (!trace) {
      this.traceService.startSpan(SpanTypes.adminQBCreateAccount);
      trace = this.traceService.getHTTPHeaderPropagators(
        SpanTypes.adminQBCreateAccount
      );
      internalTrace = true;
    }
    return this.http
      .post<QuickbooksAccount>(
        `${this.tenantConfig.api.endpoint}/admin/quickbooks/accounts`,
        account,
        {
          headers: this.processHeaders(trace),
        }
      )
      .pipe(
        catchError((err: any) => {
          internalTrace &&
            this.traceService.endSpan(SpanTypes.adminQBCreateAccount, err);
          return throwError(() => err);
        }),
        tap(() => {
          internalTrace &&
            this.traceService.endSpan(SpanTypes.adminQBCreateAccount);
        })
      );
  }
  getAuthURI(trace?: HTTPTraceHeader): Observable<string> {
    let internalTrace = false;
    if (!trace) {
      this.traceService.startSpan(SpanTypes.adminQBGetAuthUri);
      trace = this.traceService.getHTTPHeaderPropagators(
        SpanTypes.adminQBGetAuthUri
      );
      internalTrace = true;
    }
    return this.http
      .get<string>(`${this.tenantConfig.api.endpoint}/admin/quickbooks/auth`, {
        headers: this.processHeaders(trace),
      })
      .pipe(
        catchError((err: any) => {
          internalTrace &&
            this.traceService.endSpan(SpanTypes.adminQBGetAuthUri, err);
          return throwError(() => err);
        }),
        tap(() => {
          internalTrace &&
            this.traceService.endSpan(SpanTypes.adminQBGetAuthUri);
        })
      );
  }
}
